import { useNavigate } from "react-router";
import { useAuth } from "@repo/auth";
import { Button } from "@repo/components";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import type { WizardLocationState } from "../features/onboarding/onboardingApi";
import { useVenue } from "./VenueContext";
import { canAccessVenue } from "./roles";
import { useOnboardingProgress } from "./useOnboardingProgress";

const banner = css({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "3",
  px: { base: "4", md: "6" },
  py: "2.5",
  fontSize: "sm",
  colorPalette: "brand",
  color: "colorPalette.text",
  bg: "colorPalette.subtle",
  borderBottomWidth: "1px",
  borderColor: "colorPalette.muted",
});
const tag = css({
  fontWeight: "bold",
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  fontSize: "2xs",
  borderWidth: "1px",
  borderColor: "colorPalette.default",
  rounded: "sm",
  px: "1.5",
  py: "0.5",
});
const note = css({ flex: "1", minW: "16rem", color: "fg.default" });

/**
 * Persistent setup banner — shown until the venue is publicly LIVE, so a restaurateur always knows the
 * one thing standing between them and taking bookings. It reads the shared readiness signal
 * (useOnboardingProgress) and speaks the exact next action:
 *   - mandatory steps left → "finish setup" → the onboarding wizard.
 *   - READY (setup done) → "go live" → the wizard's self-service activation panel (DPA + trial + go-live).
 * Renders nothing once LIVE (or while readiness is still loading, to avoid a flash). The action is
 * MANAGER-gated (waiters can't onboard); waiters still see the status so they know why bookings are off.
 */
export function SetupBanner() {
  const { venueId } = useVenue();
  const { claims } = useAuth();
  const { t } = useTranslation();
  const { isLoading, isLive, mandatoryComplete, missingMandatory } = useOnboardingProgress();
  const navigate = useNavigate();

  if (isLoading || isLive) return null;

  const canManage = canAccessVenue(claims, venueId, "MANAGER");
  const ready = mandatoryComplete;
  const message = ready
    ? t("setup.ready")
    : t("setup.incomplete", { count: Math.max(missingMandatory.length, 1) });

  return (
    <section
      aria-label={t("setup.tag")}
      aria-live="polite"
      className={banner}
      data-testid="setup-banner"
    >
      <span className={tag}>{t("setup.tag")}</span>
      <span className={note}>{message}</span>
      {canManage &&
        (ready ? (
          // Never goes live from here: the activation panel owns the no-bookable-times warning.
          <Button
            size="sm"
            variant="solid"
            onClick={() =>
              navigate(`/v/${venueId}/onboarding`, {
                state: { activation: true } satisfies WizardLocationState,
              })
            }
          >
            {t("setup.activate")}
          </Button>
        ) : (
          <Button size="sm" variant="solid" onClick={() => navigate(`/v/${venueId}/onboarding`)}>
            {t("setup.finish")}
          </Button>
        ))}
    </section>
  );
}
